// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO y UI]
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../shared/SEO';

const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setError(t('ResetPassword.error_msg'));
        }
    }, [token, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setError('');

        if (password !== confirmPassword) {
            setError(t('RegisterForm.error_pass_match')); // Reusing key
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError(t('RegisterForm.error_pass_length')); // Reusing key
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(t('ResetPassword.success_msg'));
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(data.message || t('ResetPassword.error_resetting'));
            }
        } catch (err) {
            setError(t('ResetPassword.error_network'));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
                <SEO title={t('MetaResetPassword.title')} description={t('MetaResetPassword.description')} />
                <div className="bg-red-900/50 border border-red-500 text-red-200 p-6 rounded max-w-md text-center">
                    <h3 className="text-xl font-bold mb-2">{t('ResetPassword.error_title')}</h3>
                    <p>{t('ResetPassword.error_msg')}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="mt-4 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-white"
                    >
                        {t('RequestPassChange.return_link')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
            <SEO title={t('MetaResetPassword.title')} description={t('MetaResetPassword.description')} />
            <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6 text-center text-blue-400">{t('ResetPassword.title')}</h2>

                {message ? (
                    <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded text-center">
                        <p className="font-bold text-lg mb-2">{t('ResetPassword.success_title')}</p>
                        <p>{message}</p>
                        <p className="text-sm mt-2 text-gray-300">{t('ResetPassword.redirecting')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                                {t('ResetPassword.password_label')}
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none text-white"
                                placeholder="••••••••"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                                {t('ResetPassword.confirm_password_label')}
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none text-white"
                                placeholder="••••••••"
                            />
                        </div>

                        {error && (
                            <div className="text-red-400 text-sm text-center bg-red-900/20 p-2 rounded">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-2 px-4 rounded font-bold text-white transition-colors ${loading
                                ? 'bg-gray-600 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700'
                                }`}
                        >
                            {loading ? t('ResetPassword.button_loading') : t('ResetPassword.button')}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
